import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useMeta, pkr, waLink } from "@/lib/site";
import { Overline, Reveal } from "@/components/Reveal";

const filters = ["All", "Women", "Men", "Unisex"];

export default function SolidPerfume() {
  useMeta("Solid Perfume", "Bonheur Eternal Solid Perfume — nine long-lasting solid fragrances in 16g travel tins. PKR 1,099 each, delivered all over Pakistan.");
  const { add } = useCart();
  const [filter, setFilter] = useState("All");
  const { data: waxes = [] } = useQuery({ queryKey: ["wax"], queryFn: async () => (await api.get("/wax")).data });
  const shown = filter === "All" ? waxes : waxes.filter((w) => w.gender === filter);

  return (
    <div className="pt-32 md:pt-44 pb-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="max-w-3xl">
          <Overline>Solid Perfume</Overline>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2118] mt-4 tracking-tight">
            The scent you love,<br /><span className="italic text-[#8F6B3A]">solid and travel-ready.</span>
          </h1>
          <p className="mt-6 text-[#5A4C3C] leading-loose max-w-xl">
            All nine fragrances as long-lasting solid perfume in a 16g travel tin. A fingertip's swipe on pulse points wears close and quiet for hours — no spray, no spill, welcome in any handbag or carry-on.
          </p>
        </Reveal>
        <div className="flex gap-6 mt-14 mb-14 border-b border-[#2B2118]/[0.06] pb-4">
          {filters.map((fl) => (
            <button key={fl} data-testid={`solid-filter-${fl.toLowerCase()}`} onClick={() => setFilter(fl)}
              className={`text-[12px] tracking-[0.2em] uppercase pb-1 border-b font-medium transition-colors duration-300 ${
                filter === fl ? "text-[#6F5433] border-[#6F5433] font-semibold" : "text-[#5C4A33] border-transparent hover:text-[#2B2118]"
              }`}>
              {fl}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="solid-perfume-grid">
          {shown.map((w, i) => (
            <Reveal key={w.slug} delay={(i % 3) * 0.1}>
              <div className="group border border-[#2B2118]/[0.07] hover:border-[#8F6B3A]/40 transition-colors duration-500 bg-[#DDD3C1]/60 overflow-hidden h-full" data-testid={`solid-card-${w.slug}`}>
                <div className="relative overflow-hidden">
                  <img src={w.image} alt={w.name} loading="lazy" className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase font-medium text-[#EAD9B8] bg-[#2B2118]/70 backdrop-blur px-3 py-1.5 border border-[#EAD9B8]/25">{w.gender}</div>
                </div>
                <div className="p-7">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#8F6B3A]">Solid Perfume · 16g Travel Tin</p>
                  <h2 className="font-display text-2xl text-[#2B2118] mt-2">{w.fragrance || w.name}</h2>
                  <p className="text-sm text-[#5A4C3C] mt-3 leading-relaxed line-clamp-3">{w.description}</p>
                  <p className="font-display text-2xl font-semibold text-[#6F5433] mt-5" data-testid={`solid-price-${w.slug}`}>{pkr(w.price)} <span className="text-sm font-sans font-medium text-[#5C4A33]">· 16g</span></p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button data-testid={`solid-add-${w.slug}`}
                      onClick={() => { add({ slug: w.slug, name: w.name, price: w.price, image: w.image }); toast.success(`${w.name} added to your cart.`); }}
                      className="inline-flex items-center gap-2 rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300">
                      <ShoppingBag size={14} strokeWidth={1.5} /> Add to Cart
                    </button>
                    <a href={waLink(`Hello Bonheur Eternal, I'd like to order ${w.name} (16g tin) — ${pkr(w.price)}.`)}
                      target="_blank" rel="noopener noreferrer" data-testid={`solid-whatsapp-${w.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-[#8F6B3A]/50 text-[#8F6B3A] px-5 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300">
                      <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
                    </a>
                  </div>
                  <p className="mt-4 text-xs text-[#6E6151]">Cash on delivery · All over Pakistan</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
